import { Component, StrictMode, useEffect, type ErrorInfo, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import "@fontsource-variable/fraunces";
import "@fontsource-variable/fraunces/wght-italic.css";
import "@fontsource-variable/figtree";
import "@fontsource/caveat/600.css";
import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/ui.css";
import "./styles/pages.css";
import "./styles/puzzles.css";
import { match, navigate, usePath } from "./app/router";
import { SessionProvider, useSession } from "./app/session";
import { useStore, wipeAll } from "./app/store";
import { ToastHost, LinkButton } from "./ui/kit";
import { Shell } from "./ui/Shell";
import { Landing } from "./pages/Landing";
import { AuthPage } from "./pages/Auth";
import Today from "./pages/Today";
import Puzzles from "./pages/Puzzles";
import You from "./pages/You";
import PuzzlePage from "./pages/PuzzlePage";

function Home() {
  const { user } = useSession();
  const hasActivity = useStore((d) => Object.keys(d.progress).length > 0 || Object.keys(d.days).length > 0);
  useEffect(() => {
    if (user === null && !hasActivity) navigate("/welcome", { replace: true });
    else if (user !== undefined) navigate("/today", { replace: true });
  }, [user, hasActivity]);
  return null;
}

function NotFound() {
  return (
    <Shell>
      <div className="page-center">
        <div className="lost-card">
          <img src="/art/lost.webp" alt="Pebble the turtle, lost on a lily pad" width="420" height="280" />
          <h1>This page wandered off.</h1>
          <p className="muted">It happens to the best of us.</p>
          <LinkButton to="/today" kind="primary">Back to today</LinkButton>
        </div>
      </div>
    </Shell>
  );
}

const TITLES: Record<string, string> = { "/today": "Today", "/puzzles": "Puzzles", "/you": "You" };

/** tells screen readers where a route change took them (single-page apps don't reload) */
function RouteAnnouncer() {
  const path = usePath();
  useEffect(() => {
    const t = window.setTimeout(() => {
      const el = document.getElementById("route-announcer");
      if (el) el.textContent = document.title;
    }, 350);
    return () => window.clearTimeout(t);
  }, [path]);
  return <div id="route-announcer" className="sr-only" role="status" aria-live="polite" />;
}

class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(err: Error, info: ErrorInfo) {
    console.error("ponder crashed", err, info.componentStack);
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="page-center">
        <div className="lost-card">
          <img src="/art/lost.webp" alt="Pebble the turtle, lost on a lily pad" width="420" height="280" />
          <h1>Something went sideways.</h1>
          <p className="muted">Your progress is safe. Reloading usually fixes it.</p>
          <div className="row-gap" style={{ justifyContent: "center" }}>
            <button type="button" className="btn btn-primary" onClick={() => location.assign("/today")}>Reload</button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                if (window.confirm("Clear the puzzle data stored on this device? Anything in your account stays safe; guest progress here would be lost.")) {
                  wipeAll();
                  location.assign("/welcome");
                }
              }}
            >
              Clear this device
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function Routes() {
  const path = usePath();
  useEffect(() => {
    if (TITLES[path]) document.title = `${TITLES[path]} · Ponder`;
  }, [path]);
  let m: Record<string, string> | null;
  if (path === "/") return <Home />;
  if (path === "/welcome") return <Landing />;
  if (path === "/today") return <Today />;
  if (path === "/puzzles") return <Puzzles />;
  if (path === "/you") return <You />;
  if (path === "/login") return <AuthPage mode="login" />;
  if (path === "/signup") return <AuthPage mode="signup" />;
  if (path === "/recover") return <AuthPage mode="recover" />;
  if ((m = match("/p/:id", path))) return <PuzzlePage id={m.id} />;
  return <NotFound />;
}

/** light, dark, or whatever the device prefers */
function ThemeSync() {
  const theme = useStore((d) => d.settings.theme);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const dark = theme === "dark" || (theme === "auto" && mq.matches);
      document.documentElement.dataset.theme = dark ? "dark" : "light";
      document.querySelector('meta[name="theme-color"]')?.setAttribute("content", dark ? "#0f1d1e" : "#f3f0e3");
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [theme]);
  return null;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <SessionProvider>
        <ToastHost>
          <ThemeSync />
          <RouteAnnouncer />
          <Routes />
        </ToastHost>
      </SessionProvider>
    </ErrorBoundary>
  </StrictMode>
);

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => navigator.serviceWorker.register("/sw.js").catch(() => undefined));
}
